import { notify } from "../components/ui";
import { t } from "../i18n";
import { showSuccess, toAppError } from "../lib/feedback";
import { useApi } from "./useApi";
import { hospitalService } from "../services/hospitals";
import {
  Hospital,
  HospitalCreatePayload,
  HospitalUpdatePayload,
  DoctorHospitalsResponse,
} from "../types/hospital.types";
import { useSession } from "../context/SessionContext";
import { SUCCESS_MESSAGES } from "../i18n/success.pt-BR";

type LinkOutcome = "linked" | "already-linked";

export function useHospitals() {
  const { metadata } = useSession();

  const searchApi = useApi<Hospital[]>();
  const createApi = useApi<Hospital>();
  const updateApi = useApi<Hospital>();
  const listApi   = useApi<DoctorHospitalsResponse>();
  const linkApi   = useApi<LinkOutcome>();
  const unlinkApi = useApi<void>();

  // Autocomplete: não mostra toast de erro a cada tecla digitada,
  // uma falha de rede aqui não deve interromper o usuário.
  const searchHospitals = async (query: string) => {
    return searchApi.request(
      () => hospitalService.search(searchApi.apiFetch, query),
      { showErrorToast: false },
    );
  };

  const createHospital = async (payload: HospitalCreatePayload) => {
    return createApi.request(
      () => hospitalService.create(createApi.apiFetch, payload),
    );
  };

  // Edição é sobre o hospital em si (não sobre o vínculo) — pode afetar outros
  // médicos que compartilham o mesmo hospital (dedup por nome/email no
  // cadastro). O `detail` de um 422 (ex: e-mail inválido) já chega pronto pro
  // usuário via toast automático do useApi.
  const updateHospital = async (hospitalId: number, payload: HospitalUpdatePayload) => {
    return updateApi.request(
      () => hospitalService.update(updateApi.apiFetch, hospitalId, payload),
    );
  };

  const listMyHospitals = async () => {
    if (!metadata.email) return null;

    return listApi.request(
      () => hospitalService.listForDoctor(listApi.apiFetch, metadata.email!),
    );
  };

  // Um 409 aqui só significa que o hospital já está vinculado a este médico —
  // não é uma falha real, então é tratado dentro do próprio `fn` (sem lançar)
  // para não disparar o toast de erro automático do `request`, e sim um aviso
  // neutro. Qualquer outro erro (404, 500, rede) continua sendo propagado
  // normalmente para o tratamento padrão.
  const linkHospital = async (hospitalId: number): Promise<boolean> => {
    if (!metadata.email) return false;

    const outcome = await linkApi.request(async () => {
      try {
        await hospitalService.linkToDoctor(linkApi.apiFetch, metadata.email!, { hospital_id: hospitalId });
        return "linked";
      } catch (caught) {
        const err = toAppError(caught);
        if (err?.status === 409) return "already-linked";
        throw caught;
      }
    });

    if (outcome === null) return false;

    if (outcome === "already-linked") {
      notify({ tone: "info", title: t("links.hospital.alreadyLinked") });
    } else {
      showSuccess("hospitalLinked");
    }
    return true;
  };

  const unlinkHospital = async (hospitalId: number) => {
    if (!metadata.email) return null;

    return unlinkApi.request(
      () => hospitalService.unlinkFromDoctor(unlinkApi.apiFetch, metadata.email!, hospitalId),
      { successMessage: SUCCESS_MESSAGES.hospitalUnlinked },
    );
  };

  return {
    searchHospitals,
    createHospital,
    updateHospital,
    listMyHospitals,
    linkHospital,
    unlinkHospital,
    searching: searchApi.loading,
    creating:  createApi.loading,
    updating:  updateApi.loading,
    listing:   listApi.loading,
    linking:   linkApi.loading,
    unlinking: unlinkApi.loading,
  };
}
